import { useNavigate } from "react-router-dom";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { DataTable } from "../../../components/PromotionIQ/DataTable";
import { StatusBadge } from "../../../components/PromotionIQ/StatusBadge";
import {
  plannedPromotions,
  geoFencingMetrics,
  formatCurrency,
  formatNumber,
  getRetailerName,
  getSkuInfo,
} from "../../../data/PromotionIQ/PromotionData";
import { AlertTriangle, TrendingUp, TrendingDown, ChevronRight, Target, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import React from "react";
import { usePromotionSelection } from "../../../components/PromotionIQ/PromotionSelectionContext";

interface MonitoringData {
  promotion_id: string;
  retailer_id: string;
  sku_id: string;
  expected_revenue: number;
  actual_revenue: number;
  variance_pct: number;
  status: "On Track" | "At Risk" | "Behind";
}

const Monitoring = () => {
  const navigate = useNavigate();
  const { setSelectedPromotion } = usePromotionSelection();

  // Simulated monitoring data based on TRINITI Organics promotions
  const monitoringData: MonitoringData[] = [
    {
      promotion_id: "P201",
      retailer_id: "R1",
      sku_id: "S1",
      expected_revenue: 485000,
      actual_revenue: 502000,
      variance_pct: 3.5,
      status: "On Track",
    },
    {
      promotion_id: "P202",
      retailer_id: "R1",
      sku_id: "S2",
      expected_revenue: 420000,
      actual_revenue: 398000,
      variance_pct: -5.2,
      status: "On Track",
    },
    {
      promotion_id: "P203",
      retailer_id: "R1",
      sku_id: "S3",
      expected_revenue: 680000,
      actual_revenue: 720000,
      variance_pct: 5.9,
      status: "On Track",
    },
    {
      promotion_id: "P204",
      retailer_id: "R2",
      sku_id: "S1",
      expected_revenue: 245000,
      actual_revenue: 210000,
      variance_pct: -14.3,
      status: "At Risk",
    },
    {
      promotion_id: "P205",
      retailer_id: "R2",
      sku_id: "S3",
      expected_revenue: 275000,
      actual_revenue: 185000,
      variance_pct: -32.7,
      status: "Behind",
    },
  ];

  const chartData = monitoringData.map((d) => ({
    id: d.promotion_id,
    Expected: d.expected_revenue,
    Actual: d.actual_revenue,
  }));

  const totalExpected = monitoringData.reduce((sum, d) => sum + d.expected_revenue, 0);
  const totalActual = monitoringData.reduce((sum, d) => sum + d.actual_revenue, 0);
  const overallVariance = ((totalActual - totalExpected) / totalExpected) * 100;

  const atRiskCount = monitoringData.filter((d) => d.status === "At Risk" || d.status === "Behind").length;
  const onTrackCount = monitoringData.filter((d) => d.status === "On Track").length;

  // Social media funnel data (instead of GeoFencing for TRINITI)
  const socialFunnel = [
    { stage: "Impressions", value: geoFencingMetrics.geo_fencing.impressions },
    { stage: "Engagements", value: geoFencingMetrics.geo_fencing.store_visits },
    { stage: "Store Visits", value: geoFencingMetrics.geo_fencing.purchases },
    { stage: "Purchases", value: geoFencingMetrics.geo_fencing.incremental_units },
  ];

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="In-Cycle Monitoring"
        question="Is reality tracking the plan?"
      />

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <InteractiveCard
          label="Expected Revenue"
          value={formatCurrency(totalExpected)}
          subValue="Active promotions"
          variant="highlight"
          linkTo="/promotioniq/promotion-portfolio"
          linkLabel="View portfolio"
        />
        <InteractiveCard
          label="Actual Revenue"
          value={formatCurrency(totalActual)}
          subValue="Current tracking"
          variant={totalActual >= totalExpected ? "success" : "warning"}
          linkTo="/promotioniq/learnings"
          linkLabel="Analyze"
        />
        <InteractiveCard
          label="Overall Variance"
          value={`${overallVariance >= 0 ? "+" : ""}${overallVariance.toFixed(1)}%`}
          subValue="vs expected"
          variant={overallVariance >= -5 ? "success" : "warning"}
        />
        <InteractiveCard
          label="Promotions at Risk"
          value={atRiskCount}
          subValue={`${onTrackCount} on track`}
          variant={atRiskCount > 0 ? "warning" : "success"}
          icon={atRiskCount > 0 ? AlertTriangle : Target}
        />
      </div>

      {/* Alerts */}
      {atRiskCount > 0 && (
        <div className="border border-warning/50 bg-warning/5 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
              <div>
                <h4 className="font-semibold text-warning">Performance Alert</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {atRiskCount} promotion(s) are underperforming expectations at Whole Foods Market. 
                  P204 and P205 need immediate review - consider reducing discount depth.
                </p>
              </div>
            </div>
            <button 
              onClick={() => navigate("/promotioniq/learnings")}
              className="btn-athena text-sm"
            >
              View Root Causes
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Expected vs Actual */}
        <div className="border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="section-header">Expected vs Actual Revenue</h3>
            <button 
              onClick={() => navigate("/promotioniq/learnings")}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              Analyze Variance <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 25%)" />
                <XAxis dataKey="id" stroke="hsl(220, 10%, 55%)" fontSize={12} />
                <YAxis stroke="hsl(220, 10%, 55%)" fontSize={12} tickFormatter={(v) => `$${v / 1000}K`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(220, 18%, 13%)",
                    border: "1px solid hsl(220, 15%, 25%)",
                    borderRadius: "0",
                  }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Bar dataKey="Expected" fill="hsl(220, 10%, 40%)" name="Expected" />
                <Bar dataKey="Actual" fill="hsl(32, 95%, 55%)" name="Actual" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Social Media Funnel */}
        <div 
        onClick={() => navigate("/promotioniq/impact-panel")}
          className="border border-border bg-card p-6 cursor-pointer hover:border-primary transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="section-header">Social Media Campaign Funnel (P203)</h3>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={socialFunnel} layout="vertical" margin={{ top: 20, right: 30, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 25%)" />
                <XAxis type="number" stroke="hsl(220, 10%, 55%)" fontSize={12} tickFormatter={(v) => formatNumber(v)} />
                <YAxis dataKey="stage" type="category" stroke="hsl(220, 10%, 55%)" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(220, 18%, 13%)",
                    border: "1px solid hsl(220, 15%, 25%)",
                    borderRadius: "0",
                  }}
                  formatter={(value: number) => formatNumber(value)}
                />
                <Bar dataKey="value" fill="hsl(173, 80%, 40%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Cost per Incremental Unit</span>
            <span className="text-primary font-bold">
              ${geoFencingMetrics.geo_fencing.cost_per_incremental_unit}
            </span>
          </div>
          <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            Click for full Impact Panel analysis →
          </p>
        </div>
      </div>

      {/* Monitoring Table */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Promotion Status</h3>
          <p className="text-sm text-muted-foreground">Click any row to edit promotion</p>
        </div>
        <DataTable<MonitoringData>
          data={monitoringData}
          onRowClick={(item) => {
            setSelectedPromotion(item.promotion_id);
            navigate("/promotioniq/promotion-builder");
          }}
          columns={[
            {
              key: "promotion_id",
              header: "Promo ID",
              render: (item) => (
                <span className="font-medium text-primary">{item.promotion_id}</span>
              ),
            },
            {
              key: "retailer_id",
              header: "Retailer",
              render: (item) => (
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/promotioniq/retailer-economics/${item.retailer_id}`);
                  }}
                  className="hover:text-primary hover:underline cursor-pointer"
                >
                  {getRetailerName(item.retailer_id)}
                </span>
              ),
            },
            {
              key: "sku_id",
              header: "Product Line",
              render: (item) => getSkuInfo(item.sku_id),
            },
            {
              key: "expected_revenue",
              header: "Expected",
              render: (item) => formatCurrency(item.expected_revenue),
            },
            {
              key: "actual_revenue",
              header: "Actual",
              render: (item) => formatCurrency(item.actual_revenue),
            },
            {
              key: "variance_pct",
              header: "Variance",
              render: (item) => (
                <span className={`flex items-center gap-1 ${
                  item.variance_pct >= 0 ? "text-success" : "text-destructive"
                }`}>
                  {item.variance_pct >= 0 ? (
                    <TrendingUp className="h-4 w-4" />
                  ) : (
                    <TrendingDown className="h-4 w-4" />
                  )}
                  {item.variance_pct >= 0 ? "+" : ""}{item.variance_pct.toFixed(1)}%
                </span>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (item) => {
                const statusMap: Record<string, "Low" | "Medium" | "High"> = {
                  "On Track": "Low",
                  "At Risk": "Medium",
                  "Behind": "High",
                };
                return <StatusBadge status={statusMap[item.status]} />;
              },
            },
            {
              key: "action",
              header: "",
              render: (item) => (
                <button className="btn-athena py-1 px-3 text-xs">
                  {item.status === "On Track" ? "View" : "Fix"}
                  <ChevronRight className="h-3 w-3 ml-1" />
                </button>
              ),
            },
          ]}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate("/promotioniq/learnings")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Decision vs Outcome Review</p>
                <p className="text-sm text-muted-foreground">Did the investment thesis hold?</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/scenarios")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-success" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Scenario Comparison</p>
                <p className="text-sm text-muted-foreground">Where should investment go?</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/negotiation")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Negotiation Prep</p>
                <p className="text-sm text-muted-foreground">Prepare for retailer meetings</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This screen allows <span className="text-primary font-bold">intervention before value is lost</span>. 
          Live signals show actual vs expected lift, execution variance, and margin at risk alerts.
        </p>
      </div>
    </div>
  );
};

export default Monitoring;
